import Vue from 'vue'
import Router from 'vue-router'
import AuthLayout from '@/layouts/Auth'
import MainLayout from '@/layouts/Main'
import store from '@/store'

Vue.use(Router)

// Route meta-flag conventions used in this file:
//   authRequired: true   — gated by `beforeEach`; redirects to /auth/login if not signed in.
//   disabled:     true   — NEW (Apr 30): route is reachable component-wise but `beforeEach`
//                          bounces direct URL navigation to DEFAULT_ROUTE. Pair with
//                          `disabled: true` on the matching menu entry in services/menu/index.js
//                          (Ant Design greys + blocks the click). Used for WIP pages like /dashboard.
//   (hidden was dead metadata from a Vue admin template scaffold — removed May 14, 2026.)
const router = new Router({
  base: process.env.BASE_URL,
  // mode: 'history',
  scrollBehavior() {
    return { x: 0, y: 0 }
  },
  routes: [
    {
      path: '/forms/fill/:id',
      meta: {
        title: 'Fill Form',
        authRequired: false,
      },
      component: () => import('./views/tools/forms/FormsFill.vue'),
      props: (route) => ({ formId: route.params.id }),
    },
    {
      path: '/',
      redirect: '/tools/contracts/all',
      component: MainLayout,
      meta: {
        authRequired: true,
      },
      children: [
        // Dashboards
        {
          path: '/dashboard',
          meta: {
            title: 'Dashboard',
            authRequired: true,
          },
          component: () => import('./views/dashboard/v1'),
        },
        // Mass Messaging
        {
          // Optional id so an open message survives refresh and can be linked
          // (#/messages/42). The menu entry stays '/messages'.
          path: '/messages/:id?',
          meta: {
            title: 'Messages',
            authRequired: true,
          },
          component: () => import('./views/tools/messaging'),
        },
        // Reports. The report builder lives in the Reports tab of Analytics;
        // the old address keeps working for anyone who bookmarked it.
        {
          path: '/reports',
          redirect: { path: '/analytics', query: { tab: 'custom-reports' } },
        },
        // Analytics
        {
          path: '/analytics',
          meta: {
            title: 'Analytics',
            authRequired: true,
          },
          component: () => import('./views/tools/analytics'),
        },
        {
          path: '/aya',
          meta: {
            title: 'Aya',
            authRequired: true,
          },
          component: () => import('./views/aya'),
        },
        //Schematic
        {
          path: '/schematic/:routeId?',
          meta: {
            title: 'Schematic',
            authRequired: true,
          },
          component: () => import('./views/tools/schematic'),
        },
        // Attendance
        {
          path: '/attendance',
          meta: {
            title: 'Attendance',
            authRequired: true,
          },
          component: () => import('./views/tools/attendance'),
        },
        {
          path: '/forms',
          meta: {
            title: 'Forms',
            authRequired: true,
          },
          component: () => import('./views/tools/forms'),
        },
        {
          path: '/forms/review',
          meta: {
            title: 'Review submissions',
            authRequired: true,
          },
          component: () => import('./views/tools/forms/FormsReview'),
        },
        {
          path: '/forms/create',
          meta: {
            title: 'Create New',
            authRequired: true,
          },
          component: () => import('./views/tools/forms/CreateForm'),
        },
        {
          path: '/forms/create/aya',
          meta: {
            title: 'Talk to Aya',
            authRequired: true,
          },
          component: () => import('./views/tools/forms/AyaMode'),
        },
        // Tools
        {
          path: '/tools/students/all',
          meta: {
            title: 'All Students',
            authRequired: true,
          },
          component: () => import('./views/tools/allstudents'),
        },
        {
          path: '/tools/buildings/all',
          meta: {
            title: 'All Buildings',
            authRequired: true,
          },
          component: () => import('./views/tools/allbuildings'),
        },
        {
          path: '/tools/student/view',
          meta: {
            title: 'Student',
            authRequired: true,
          },
          component: () => import('./views/tools/student'),
        },
        {
          path: '/tools/employees/all',
          meta: {
            title: 'All Employees',
            authRequired: true,
          },
          component: () => import('./views/tools/allemployees'),
        },
        {
          path: '/tools/employee/view',
          meta: {
            title: 'Employee',
            authRequired: true,
          },
          component: () => import('./views/tools/employee'),
        },
        {
          path: '/tools/maintenance',
          meta: {
            title: 'Maintenance',
            authRequired: true,
          },
          component: () => import('./views/tools/maintenance'),
        },
        {
          path: '/tools/maintenance/fuel/report',
          meta: {
            title: 'Fuel Reporting',
            authRequired: true,
          },
          component: () => import('./views/tools/maintenance/FuelReporting'),
        },
        {
          path: '/tools/maintenance/fuel/report/multiple',
          meta: {
            title: 'Multiple Fuel Reports',
            authRequired: true,
          },
          component: () => import('./views/tools/maintenance/FuelMultipleReporting'),
        },
        {
          path: '/tools/maintenance/dvir/report',
          meta: {
            title: 'DVIR Reporting',
            authRequired: true,
          },
          component: () => import('./views/tools/maintenance/DvirReporting'),
        },
        {
          path: '/tools/maintenance/services/report',
          meta: {
            title: 'Services Work Order',
            authRequired: true,
          },
          component: () => import('./views/tools/maintenance/ServicesReporting'),
        },
        {
          path: '/tools/maintenance/dvir/report/multiple',
          meta: {
            title: 'Multiple DVIR Reports',
            authRequired: true,
          },
          component: () => import('./views/tools/maintenance/DvirMultipleReporting'),
        },
        {
          path: '/tools/vehicles/all',
          meta: {
            title: 'All Vehicles',
            authRequired: true,
          },
          component: () => import('./views/tools/vehicles'),
        },
        {
          path: '/tools/vehicle/view',
          meta: {
            title: 'Vehicle',
            authRequired: true,
          },
          component: () => import('./views/tools/vehicle'),
        },
        {
          path: '/tools/contracts/all',
          meta: {
            title: 'All Contracts',
            authRequired: true,
          },
          component: () => import('./views/tools/contracts'),
        },
        {
          path: '/tools/contract/view',
          meta: {
            title: 'Contract',
            authRequired: true,
          },
          component: () => import('./views/tools/contract'),
        },
        {
          path: '/tools/districts/all',
          meta: {
            title: 'All Districts',
            authRequired: true,
          },
          component: () => import('./views/tools/districts'),
        },
        {
          path: '/tools/district/view',
          meta: {
            title: 'District',
            authRequired: true,
          },
          component: () => import('./views/tools/district'),
        },
        {
          path: '/tools/routes/all',
          meta: {
            title: 'All Routes',
            authRequired: true,
          },
          component: () => import('./views/tools/routing/routing'),
        },
        {
          path: '/tools/telematics/all',
          meta: {
            title: 'All Telematics',
            authRequired: true,
          },
          component: () => import('./views/tools/telematics'),
        },
        {
          path: '/tools/telematics/report/view',
          meta: {
            title: 'Report',
            authRequired: true,
          },
          component: () => import('./views/tools/report'),
        },
        // Preferences
        {
          path: '/preferences/profile',
          meta: {
            title: 'Profile',
            authRequired: true,
          },
          component: () => import('./views/preferences/profile'),
        },
        {
          path: '/preferences/settings',
          meta: {
            title: 'Settings',
            authRequired: true,
          },
          component: () => import('./views/preferences/settings'),
        },
        {
          path: '/preferences/companyprofile',
          meta: {
            title: 'Company Profile',
            authRequired: true,
          },
          component: () => import('./views/preferences/companyprofile'),
        },
        {
          path: '/preferences/organizationprofile',
          meta: {
            title: 'Organization Profile',
            authRequired: true,
          },
          component: () => import('./views/preferences/organizationprofile'),
        },
        {
          path: '/preferences/devices',
          meta: {
            title: 'All Devices',
            authRequired: true,
          },
          component: () => import('./views/preferences/devices'),
        },
        {
          path: '/preferences/facilities',
          meta: {
            title: 'All Facilities',
            authRequired: true,
          },
          component: () => import('./views/preferences/facilities'),
        },
        {
          path: '/preferences/billing',
          meta: {
            title: 'Billing',
            authRequired: true,
          },
          component: () => import('./views/preferences/billing'),
        },
        {
          path: '/preferences/invite',
          meta: {
            title: 'Invite User',
            authRequired: true,
          },
          component: () => import('./views/preferences/invite/InviteWizard'),
        },
        {
          path: '/preferences/access/departments',
          meta: {
            title: 'Departments',
            authRequired: true,
          },
          component: () => import('./views/preferences/access/departments'),
        },
        {
          path: '/preferences/access/team',
          meta: {
            title: 'Team',
            authRequired: true,
          },
          component: () => import('./views/preferences/access/employees'),
        },
        {
          path: '/preferences/access/parents',
          meta: {
            title: 'Parents',
            authRequired: true,
          },
          component: () => import('./views/preferences/access/parents'),
        },
        {
          path: '/preferences/access/partners',
          meta: {
            title: 'Partners',
            authRequired: true,
          },
          component: () => import('./views/preferences/access/partners'),
        },
        {
          path: '/preferences/access/roles',
          meta: {
            title: 'Roles',
            authRequired: true,
          },
          component: () => import('./views/preferences/access/roles'),
        },
        {
          path: '/preferences/security',
          meta: {
            title: 'Security',
            authRequired: true,
          },
          component: () => import('./views/preferences/security'),
        },
        {
          path: '/preferences/vendors',
          meta: {
            title: 'Vendors',
            authRequired: true,
          },
          component: () => import('./views/preferences/vendors'),
        },
        {
          path: '/preferences/vendors/new',
          meta: {
            title: 'Create Vendor',
            authRequired: true,
          },
          component: () => import('./views/preferences/vendors/VendorForm'),
        },
        {
          path: '/preferences/vendors/:id',
          meta: {
            title: 'Vendor Details',
            authRequired: true,
          },
          component: () => import('./views/preferences/vendors/VendorDetail'),
        },
        {
          path: '/preferences/vendors/:id/edit',
          meta: {
            title: 'Edit Vendor',
            authRequired: true,
          },
          component: () => import('./views/preferences/vendors/VendorForm'),
        },
        // Drive
        {
          path: '/drive',
          meta: {
            title: 'Drive',
            authRequired: true,
          },
          component: () => import('./views/tools/drive'),
        },
        // Apps
        {
          path: '/calendar',
          meta: {
            title: 'Apps / Calendar',
            authRequired: true,
          },
          component: () => import('./views/tools/calendar'),
        },
        // Admin
        {
          path: '/admin3366',
          meta: {
            title: 'Administration',
            authRequired: true,
          },
          component: () => import('./views/admin'),
        },
      ],
    },
    // System Pages
    {
      path: '/auth',
      component: AuthLayout,
      meta: {
        authRequired: false,
      },
      redirect: 'auth/login',
      children: [
        {
          path: '/auth/404',
          meta: {
            title: 'Error 404',
          },
          component: () => import('./views/auth/404'),
        },
        {
          path: '/auth/500',
          meta: {
            title: 'Error 500',
          },
          component: () => import('./views/auth/500'),
        },
        {
          path: '/auth/login',
          meta: {
            title: 'Sign In',
          },
          component: () => import('./views/auth/login'),
        },
        {
          path: '/auth/register',
          meta: {
            title: 'Sign Up',
          },
          component: () => import('./views/auth/register'),
        },
        {
          path: '/auth/2fa',
          meta: {
            title: 'Two-Step Verification',
          },
          component: () => import('./views/auth/2FA'),
        },
        {
          path: '/auth/forgot-password',
          meta: {
            title: 'Forgot Password',
          },
          component: () => import('./views/auth/forgot-password'),
        },
        {
          path: '/auth/forgot-password-resend',
          meta: {
            title: 'Forgot Password Resend',
          },
          component: () => import('./views/auth/forgot-password-resend'),
        },
        {
          path: '/auth/forgot-password-submit',
          meta: {
            title: 'Forgot Password Submit',
          },
          component: () => import('./views/auth/forgot-password-submit'),
        },
        {
          path: '/auth/accept-invite',
          meta: {
            title: 'Accept Invitation',
          },
          component: () => import('./views/auth/accept-invite'),
        },
        {
          path: '/auth/lockscreen',
          meta: {
            title: 'Lockscreen',
          },
          component: () => import('./views/auth/lockscreen'),
        },
      ],
    },
    {
      path: '/onboarding',
      component: () => import('./views/onboarding'),
      meta: {
        title: 'Onboarding',
        authRequired: false,
      },
    },
    // Public Status (no auth)
    {
      path: '/status',
      component: () => import('./views/status'),
      meta: {
        title: 'System Status',
        authRequired: false,
      },
    },
    // Redirect to 404
    {
      path: '*',
      redirect: 'auth/404',
    },
  ],
})

/**
 * Map route path prefixes to the resource they require.
 * Routes not listed here are accessible to any authenticated user.
 */
const ROUTE_RESOURCE = [
  ['/analytics', 'analytics'],
  ['/attendance', 'attendance'],
  ['/schematic', 'schematic'],
  ['/calendar', 'calendar'],
  ['/forms', 'documents'],
  ['/drive', 'documents'],
  ['/tools/students', 'students'],
  ['/tools/student', 'students'],
  ['/tools/buildings', 'buildings'],
  ['/tools/employees', 'employees'],
  ['/tools/employee', 'employees'],
  ['/tools/vehicles', 'vehicles'],
  ['/tools/vehicle', 'vehicles'],
  ['/tools/contracts', 'contracts'],
  ['/tools/contract', 'contracts'],
  ['/tools/districts', 'districts'],
  ['/tools/district', 'districts'],
  ['/tools/routes', 'routings'],
  ['/tools/telematics', 'telematics'],
  ['/tools/maintenance', 'vehicles'],
  ['/preferences/settings', 'settings'],
  ['/preferences/billing', 'billing'],
  ['/preferences/organizationProfile', 'organizations'],
  ['/preferences/devices', 'devices'],
  ['/preferences/facilities', 'facilities'],
  ['/preferences/access/team', 'team'],
  ['/preferences/access/parents', 'parents'],
  ['/preferences/access/partners', 'partners'],
  ['/preferences/access/roles', 'permission_overrides'],
  ['/preferences/access/departments', 'departments'],
  ['/preferences/access', 'team'],
  ['/preferences/invite', 'team'],
  ['/preferences/vendors', 'settings'],
]

function requiredResource(path) {
  for (const [prefix, resource] of ROUTE_RESOURCE) {
    if (path === prefix || path.startsWith(prefix + '/')) return resource
  }
  return null
}

const DEFAULT_ROUTE = '/tools/contracts/all'

router.beforeEach((to, from, next) => {
  // Routes flagged as disabled (e.g. WIP pages greyed out in the menu) should
  // not be reachable via direct URL either. Bounce to the default landing page.
  if (to.matched.some((record) => record.meta.disabled)) {
    if (to.path !== DEFAULT_ROUTE) {
      next({ path: DEFAULT_ROUTE })
    } else {
      next()
    }
    return
  }
  if (to.matched.some((record) => record.meta.authRequired)) {
    if (!store.state.user.authorized) {
      const hasToken = localStorage.getItem('accessToken')
      if (hasToken) {
        // Token exists but authorized flag not yet set (page reload).
        // Let navigation through — LOAD_CURRENT_ACCOUNT will verify the
        // token and redirect to login if it's expired.
        next()
      } else {
        next({
          path: '/auth/login',
          query: { redirect: to.fullPath },
        })
      }
    } else {
      const perms = store.state.permissions
      if (perms.loaded) {
        const resource = requiredResource(to.path)
        if (resource && !store.getters['permissions/canView'](resource)) {
          if (to.path !== DEFAULT_ROUTE) {
            next({ path: DEFAULT_ROUTE })
          } else {
            next()
          }
          return
        }
      }
      next()
    }
  } else {
    next()
  }
})

export default router
